import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Download, Upload } from 'lucide-react';
import type { TableSchemaCategory } from '@/domains/diagram/shapes/table/tableSchema.types';
import type { CustomTableSchema } from '@/domains/diagram/shapes/table/customTableSchemas';
import { exportSchemaAsJSON, importSchemaFromJSON, saveCustomSchema } from '@/domains/diagram/shapes/table/customTableSchemas';
import { toast } from 'sonner';

interface SaveTableTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, category: TableSchemaCategory) => void;
  initialName?: string;
  initialCategory?: TableSchemaCategory;
  schemaToExport?: CustomTableSchema | null;
  showExport?: boolean;
  showImport?: boolean;
}

const CATEGORIES: Array<{ value: TableSchemaCategory; labelKey: string }> = [
  { value: 'strategy', labelKey: 'tableTemplates.categories.strategy' },
  { value: 'agile', labelKey: 'tableTemplates.categories.agile' },
  { value: 'planning', labelKey: 'tableTemplates.categories.planning' },
  { value: 'analysis', labelKey: 'tableTemplates.categories.analysis' },
  { value: 'custom', labelKey: 'tableTemplates.categories.custom' },
];

export const SaveTableTemplateDialog: React.FC<SaveTableTemplateDialogProps> = ({
  open,
  onOpenChange,
  onSave,
  initialName = '',
  initialCategory = 'custom',
  schemaToExport = null,
  showExport = false,
  showImport = false,
}) => {
  const { t } = useTranslation('common');
  const [name, setName] = React.useState(initialName);
  const [category, setCategory] = React.useState<TableSchemaCategory>(initialCategory);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setName(initialName);
      setCategory(initialCategory);
    }
  }, [open, initialName, initialCategory]);

  const handleSave = () => {
    if (name.trim().length === 0) return;
    onSave(name.trim(), category);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleExport = () => {
    if (!schemaToExport) return;
    const json = exportSchemaAsJSON(schemaToExport);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${schemaToExport.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = event.target?.result as string;
        const imported = importSchemaFromJSON(json);
        if (imported) {
          saveCustomSchema(imported);
          const evt = new CustomEvent('table-template-imported', { detail: { schemaId: imported.id, name: imported.name } });
          window.dispatchEvent(evt);
          onOpenChange(false);
        } else {
          toast.error(t('tableTemplates.importFailed'));
        }
      } catch (error) {
        console.error('Failed to import template:', error);
        toast.error('Failed to import template. Please check the file format.');
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('tableTemplates.saveAsTemplate')}</DialogTitle>
          <DialogDescription>
            {t('tableTemplates.saveDialogDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="template-name">{t('tableTemplates.templateName')}</Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('tableTemplates.templateNamePlaceholder')}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim().length > 0) {
                  handleSave();
                }
              }}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="template-category">{t('tableTemplates.category')}</Label>
            <Select value={category} onValueChange={(value) => setCategory(value as TableSchemaCategory)}>
              <SelectTrigger id="template-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {t(cat.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2">
            {showExport && schemaToExport && (
              <Button variant="outline" onClick={handleExport} className="gap-2">
                <Download className="w-4 h-4" />
                {t('actions.export')}
              </Button>
            )}
            {showImport && (
              <>
                <Button variant="outline" onClick={handleImport} className="gap-2">
                  <Upload className="w-4 h-4" />
                  {t('actions.import')}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              {t('actions.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={name.trim().length === 0}>
              {t('tableTemplates.saveTemplate')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
